import React, { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Button } from "react-bootstrap";
import styled from "styled-components";
import { colors } from "utils";
import { GenerateReportConfirmation } from "./GenerateReportConfirmation";
import { EmptyState, ErrorState, LoadingState } from "./SectionStates";
import { SESSION_COLUMNS, STATUSES } from "./sessionColumns";
import { BORDER, Card, CardTitle, WHITE } from "./styles";
import { useReportStarts } from "./useReportStarts";

const ROW_BORDER = "#F3F4F6";
const TITLE_ID = "teacher-reports-metrics-sessions-title";

const TableCard = styled(Card)`
  overflow: hidden;
  padding: 0;
`;

const TableHead = styled.div`
  align-items: center;
  border-bottom: 1px solid ${BORDER};
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
  padding: 12px 16px;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Pill = styled.button`
  background: ${(p) => (p.$selected ? colors.blue500 : WHITE)};
  border: 1px solid ${(p) => (p.$selected ? colors.blue500 : BORDER)};
  border-radius: 999px;
  color: ${(p) => (p.$selected ? WHITE : colors.milkGlass950)};
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
`;

const Scroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  border-collapse: collapse;
  font-size: 13px;
  width: 100%;
`;

const Th = styled.th`
  background: ${colors.milkGlass200};
  border-bottom: 1px solid ${BORDER};
  cursor: ${(p) => (p.$sortable ? "pointer" : "default")};
  font-weight: 700;
  padding: 10px 12px;
  text-align: left;
  white-space: nowrap;
`;

const Td = styled.td`
  border-bottom: 1px solid ${ROW_BORDER};
  padding: 8px 12px;
  text-align: ${(p) => p.$align || "left"};
  white-space: nowrap;
`;

const Footer = styled.div`
  align-items: center;
  border-top: 1px solid ${BORDER};
  color: ${colors.milkGlass950};
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  padding: 10px 16px;
`;

const NO_STATUS_MATCH = "No sessions with this status in this period.";
const NO_FILTER_MATCH = "No sessions match this filter in this period.";

const ALL_PILL = { label: "All", matches: () => true, emptyText: NO_STATUS_MATCH };
const PILLS = [
  ALL_PILL,
  ...Object.entries(STATUSES).map(([status, { label }]) => ({
    label,
    matches: (session) => session.status === status,
    emptyText: NO_STATUS_MATCH
  })),
  { label: "Needs Attention", matches: (session) => session.needs_attention, emptyText: NO_FILTER_MATCH },
  { label: "Late", matches: (session) => session.is_late, emptyText: NO_FILTER_MATCH }
];

const NEWEST_FIRST = [{ id: "ended", desc: true }];
const SORT_MARKS = { asc: " ▲", desc: " ▼" };
const PAGE_SIZE = 50;

const emptyText = (sessions, pill) => (sessions.length === 0 ? "No sessions ended in this period." : pill.emptyText);

export const SessionsTable = ({ status, sessions, asOf, onRetry, onReportStarted }) => {
  const [chosenPill, setChosenPill] = useState(ALL_PILL);
  const [sorting, setSorting] = useState(NEWEST_FIRST);
  const [pageIndex, setPageIndex] = useState(0);
  const [keptInViewIds, setKeptInViewIds] = useState([]);
  const [sessionToConfirm, setSessionToConfirm] = useState(null);

  const reportStarts = useReportStarts((sessionId) => {
    setKeptInViewIds((ids) => [...ids, sessionId]);
    onReportStarted(sessionId);
  });
  const { forgetFailures } = reportStarts;

  const isLoadingNewList = status === "loading";
  useEffect(() => {
    if (!isLoadingNewList) return;
    setPageIndex(0);
    setKeptInViewIds([]);
    forgetFailures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingNewList]);

  const shownSessions = useMemo(
    () => sessions.filter((session) => chosenPill.matches(session) || keptInViewIds.includes(session.session_id)),
    [sessions, chosenPill, keptInViewIds]
  );

  const closeConfirmation = () => setSessionToConfirm(null);

  const requestReport = (session) => {
    if (session.status === "completed") setSessionToConfirm(session);
    else reportStarts.start(session.session_id);
  };

  const confirmReport = () => {
    if (!sessionToConfirm) return;
    reportStarts.start(sessionToConfirm.session_id);
    closeConfirmation();
  };

  const table = useReactTable({
    data: shownSessions,
    columns: SESSION_COLUMNS,
    meta: {
      asOf,
      startingIds: reportStarts.startingIds,
      startFailures: reportStarts.startFailures,
      onGenerate: requestReport
    },
    state: { pagination: { pageIndex, pageSize: PAGE_SIZE }, sorting },
    defaultColumn: { enableSorting: false },
    enableSortingRemoval: false,
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (session) => session.session_id
  });

  const choosePill = (pill) => {
    setChosenPill(pill);
    setKeptInViewIds([]);
    setPageIndex(0);
  };

  const sortBy = (column) => {
    setSorting([{ id: column.id, desc: column.getIsSorted() !== "desc" }]);
    setPageIndex(0);
  };

  const pageRows = table.getRowModel().rows;
  const firstShown = pageIndex * PAGE_SIZE + 1;

  return (
    <TableCard aria-labelledby={TITLE_ID}>
      <TableHead>
        <CardTitle id={TITLE_ID} style={{ margin: 0 }}>
          Sessions
        </CardTitle>
        {status === "ready" && (
          <Pills role="group" aria-label="Status">
            {PILLS.map((pill) => (
              <Pill
                key={pill.label}
                type="button"
                aria-pressed={pill === chosenPill}
                $selected={pill === chosenPill}
                onClick={() => choosePill(pill)}>
                {pill.label} {sessions.filter(pill.matches).length}
              </Pill>
            ))}
          </Pills>
        )}
      </TableHead>
      {status === "loading" && <LoadingState />}
      {status === "error" && <ErrorState onRetry={onRetry} />}
      {status === "ready" && pageRows.length === 0 && <EmptyState>{emptyText(sessions, chosenPill)}</EmptyState>}
      {pageRows.length > 0 && (
        <>
          <Scroll>
            <Table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        $sortable={header.column.getCanSort()}
                        onClick={header.column.getCanSort() ? () => sortBy(header.column) : undefined}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {SORT_MARKS[header.column.getIsSorted()]}
                      </Th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Td key={cell.id} $align={cell.column.columnDef.meta?.align}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroll>
          <Footer>
            <span>
              Showing {firstShown} to {firstShown + pageRows.length - 1} of {shownSessions.length}
            </span>
            <span>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={!table.getCanPreviousPage()}
                onClick={() => setPageIndex(pageIndex - 1)}>
                Previous
              </Button>{" "}
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={!table.getCanNextPage()}
                onClick={() => setPageIndex(pageIndex + 1)}>
                Next
              </Button>
            </span>
          </Footer>
        </>
      )}
      <GenerateReportConfirmation
        show={sessionToConfirm !== null}
        onCancel={closeConfirmation}
        onConfirm={confirmReport}
      />
    </TableCard>
  );
};
